import React, { useState, useEffect } from "react";
import { Modal, Form, Button, Row, Col } from "react-bootstrap";
import employeeApi from "../../services/employeeapi";
import { showConfirm } from "react-confirm-prompt";
import branchApi from "../../services/branchapi";
import AlertDialog from "../../components/modal/AlertDialog";

const EmployeeCreateForm = ({ show, handleClose, handleCreateEmployee, editingEmployeeId, departments = [],
    designations = [],
    branches = [],
    employees = [] }) => {
    const [employeeForm, setEmployeeForm] = useState({
        name: "",
        designation: "",
        department: "",
        deptHead: "",
        superadmin: "",
        branchId: "",
        jobTitle: "",
        qualification: "",
        reportTo: "",
        email: "",
        mobile: "",
        address: "",
        pincode: "",
        area: "",
        city: "",
        state: "",
        country: "",
    });

    const [errors, setErrors] = useState([]);
    const [isAlertVisible, setIsAlertVisible] = useState(false);
    const [alertMessage, setAlertMessage] = useState("");
    const [alertTitle, setAlertTitle] = useState("");
    const [isDataLoading, setIsDataLoading] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [dotCount, setDotCount] = useState(0);


    useEffect(() => {
        let interval;
        if (isSubmitting) {
            interval = setInterval(() => {
                setDotCount(prev => (prev + 1) % 4); // Loops from 0 to 3
            }, 500); // Speed of animation (500ms)
        } else {
            setDotCount(0); // Reset when not submitting
        }

        return () => clearInterval(interval); // Cleanup on unmount
    }, [isSubmitting]);


    useEffect(() => {
        if (editingEmployeeId) {
            setIsDataLoading(true);
            const fetchEmployeeData = async () => {
                try {
                    const response = await employeeApi.getEmployeeDetails({ employeeId: editingEmployeeId });
                    const employeeData = response.data.employee ?? null;
                    // console.log(employeeData, 'employeeData');
                    if (employeeData) {
                        setEmployeeForm({
                            name: employeeData.name,
                            designation: employeeData.designation,
                            department: employeeData.department,
                            deptHead: employeeData.deptHead,
                            superadmin: employeeData.superadmin,
                            branchId: employeeData.branchId,
                            jobTitle: employeeData.jobTitle,
                            qualification: employeeData.qualification,
                            reportTo: employeeData.reportTo,
                            email: employeeData.email,
                            mobile: employeeData.mobile,
                            address: employeeData.address,
                            pincode: employeeData.pincode,
                            area: employeeData.area,
                            city: employeeData.city,
                            state: employeeData.state,
                            country: employeeData.country,
                        });
                    }
                } catch (error) {
                    console.error("Failed to fetch employee details", error);
                }
                finally {
                    setIsDataLoading(false); // End loader
                }
            };

            fetchEmployeeData();
        } else {
            setEmployeeForm({
                name: "",
                designation: "",
                department: "",
                deptHead: "",
                superadmin: "",
                branchId: "",
                jobTitle: "",
                qualification: "",
                reportTo: "",
                email: "",
                mobile: "",
                address: "",
                pincode: "",
                area: "",
                city: "",
                state: "",
                country: "",
            });
        }
    }, [editingEmployeeId]);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setEmployeeForm({
            ...employeeForm,
            [name]: name === "branchId" ? parseInt(value) : value,
        });
    };

    const validateEmployeeForm = () => {
        const newErrors = {};
        if (!employeeForm.name) newErrors.name = "Name is required";
        if (!employeeForm.mobile) newErrors.mobile = "Mobile is required";  // Mobile is mandatory
        if (!employeeForm.jobTitle) newErrors.jobTitle = "Job Title is required";  // Job Title is mandatory
        if (!employeeForm.department) newErrors.department = "Department is required";
        if (!employeeForm.designation) newErrors.designation = "Designation is required";
        if (!employeeForm.branchId) newErrors.branchId = "Branch is required";
        return newErrors;
    };


    const handleCreateOrUpdate = async () => {
        setIsSubmitting(true);

        const formErrors = validateEmployeeForm();
        if (Object.keys(formErrors).length > 0) {
            setErrors(formErrors);
            setIsSubmitting(false);
        } else {
            setErrors({});
            try {
                let response;
                if (editingEmployeeId) {
                    // Update employee if editingEmployeeId is present
                    employeeForm.id = editingEmployeeId;
                    response = await employeeApi.createEmployee(employeeForm);


                } else {
                    // Create new employee if editingEmployeeId is not present
                    response = await employeeApi.createEmployee(employeeForm);
                }

                if (response.status === 'SUCCESS') {
                    setIsAlertVisible(1);
                    setAlertTitle('Success');
                    setAlertMessage(editingEmployeeId ? "Employee updated successfully!" : "Employee created successfully!");
                    // handleClose();
                    // handleCreateEmployee(); // Refresh the employee list
                }
                else {
                    setAlertTitle('Oops !');
                    setIsAlertVisible(1);
                    setAlertMessage(response.message || "Failed to create/update employee.");

                }
                // if (response.success) {
                //     showConfirm("Success", {
                //         description:
                //             type: "success",
                //     }).then(() => {
                //         handleCreateEmployee(); // Refresh the employee list
                //         handleClose(); // Close the modal
                //     });
                // } else {
                //     showConfirm("Oops!", {
                //         description: response.message || "Failed to create/update employee.",
                //         type: "warning",
                //     });
                // }
            } catch (error) {
                setAlertTitle('Oops !');
                setAlertMessage("Something went wrong. Please try again.");
                setIsAlertVisible(true);
            } finally {
                setIsSubmitting(false); // Stop loader after API call
            }
        }
    };



    return (
        <Modal show={show} onHide={handleClose} size="lg">
            <Modal.Header closeButton>
                <Modal.Title>{editingEmployeeId ? "Edit Employee" : "Create Employee"}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <Form>
                    <Row>
                        <Col md={6}>
                            <Form.Group>
                                <Form.Label>Employee Name</Form.Label>
                                <Form.Control
                                    type="text"
                                    name="name"
                                    value={employeeForm.name}
                                    onChange={handleInputChange}
                                    isInvalid={!!errors.name}
                                />
                                <Form.Control.Feedback type="invalid">
                                    {errors.name}
                                </Form.Control.Feedback>
                            </Form.Group>
                        </Col>
                        <Col md={6}>
                            <Form.Group>
                                <Form.Label>Email</Form.Label>
                                <Form.Control
                                    type="email"
                                    name="email"
                                    value={employeeForm.email}
                                    onChange={handleInputChange}
                                />
                            </Form.Group>
                        </Col>
                    </Row>
                    <Row>
                        <Col md={6}>
                            <Form.Group>
                                <Form.Label>Mobile</Form.Label>
                                <Form.Control
                                    type="text"
                                    name="mobile"
                                    value={employeeForm.mobile}
                                    onChange={handleInputChange}
                                    isInvalid={!!errors.mobile}
                                />
                                <Form.Control.Feedback type="invalid">
                                    {errors.mobile}
                                </Form.Control.Feedback>
                            </Form.Group>
                        </Col>

                        <Col md={6}>
                            <Form.Group>
                                <Form.Label>Job Title</Form.Label>
                                <Form.Control
                                    type="text"
                                    name="jobTitle"
                                    value={employeeForm.jobTitle}
                                    onChange={handleInputChange}
                                    isInvalid={!!errors.jobTitle}
                                />
                                <Form.Control.Feedback type="invalid">
                                    {errors.jobTitle}
                                </Form.Control.Feedback>
                            </Form.Group>
                        </Col>
                    </Row>

                    <Row>
                        <Col md={6}>
                            <Form.Group>
                                <Form.Label>Department</Form.Label>
                                <Form.Control
                                    as="select"
                                    name="department"
                                    value={employeeForm.department}
                                    onChange={handleInputChange}
                                    isInvalid={!!errors.department}
                                >
                                    <option value="">Select Department</option>
                                    {departments.map((dept) => (
                                        <option key={dept.id} value={dept.id}>
                                            {dept.name}
                                        </option>
                                    ))}
                                </Form.Control>
                                <Form.Control.Feedback type="invalid">
                                    {errors.department}
                                </Form.Control.Feedback>
                            </Form.Group>
                        </Col>

                        <Col md={6}>
                            <Form.Group>
                                <Form.Label>Designation</Form.Label>
                                <Form.Control
                                    as="select"
                                    name="designation"
                                    value={employeeForm.designation}
                                    onChange={handleInputChange}
                                    isInvalid={!!errors.designation}
                                >
                                    <option value="">Select Designation</option>
                                    {designations.map((desg) => (
                                        <option key={desg.id} value={desg.id}>
                                            {desg.name}
                                        </option>
                                    ))}
                                </Form.Control>
                                <Form.Control.Feedback type="invalid">
                                    {errors.designation}
                                </Form.Control.Feedback>
                            </Form.Group>
                        </Col>
                    </Row>

                    <Row>
                        <Col md={6}>
                            <Form.Group>
                                <Form.Label>Branch</Form.Label>
                                <Form.Control
                                    as="select"
                                    name="branchId"
                                    value={employeeForm.branchId?.toString() || ""}
                                    onChange={handleInputChange}
                                    isInvalid={!!errors.branchId}
                                >
                                    <option value="">Select Branch</option>
                                    {branches.map((branch) => (
                                        <option key={branch.branchId} value={branch.branchId.toString()}>
                                            {branch.branchName}
                                        </option>
                                    ))}
                                </Form.Control>
                                <Form.Control.Feedback type="invalid">
                                    {errors.branchId}
                                </Form.Control.Feedback>
                            </Form.Group>
                        </Col>

                        <Col md={6}>
                            <Form.Group>
                                <Form.Label>Qualification</Form.Label>
                                <Form.Control
                                    type="text"
                                    name="qualification"
                                    value={employeeForm.qualification}
                                    onChange={handleInputChange}
                                />
                            </Form.Group>
                        </Col>
                    </Row>

                    <Row>
                        <Col md={6}>
                            <Form.Group>
                                <Form.Label>Report To</Form.Label>
                                <Form.Control
                                    as="select"
                                    name="reportTo"
                                    value={employeeForm.reportTo}
                                    onChange={handleInputChange}
                                >
                                    <option value="">Select Employee</option>
                                    {employees.map((emp) => (
                                        <option key={emp.id} value={emp.id}>
                                            {emp.name}
                                        </option>
                                    ))}
                                </Form.Control>
                            </Form.Group>
                        </Col>

                        <Col md={6}>
                            <Form.Group>
                                <Form.Label>Dept Head</Form.Label>
                                <Form.Control
                                    as="select"
                                    name="deptHead"
                                    value={employeeForm.deptHead}
                                    onChange={handleInputChange}
                                >
                                    <option value="">Select Department Head</option>
                                    {employees.map((emp) => (
                                        <option key={emp.id} value={emp.id}>
                                            {emp.name}
                                        </option>
                                    ))}
                                </Form.Control>
                            </Form.Group>
                        </Col>
                    </Row>

                    <Row>
                        <Col md={6}>
                            <Form.Group>
                                <Form.Label>Super Admin</Form.Label>
                                <Form.Control
                                    as="select"
                                    name="superadmin"
                                    value={employeeForm.superadmin}
                                    onChange={handleInputChange}
                                >
                                    <option value="">Branch Admin</option>
                                    <option value="1">Yes</option>
                                    <option value="0">No</option>
                                </Form.Control>
                            </Form.Group>
                        </Col>
                        <Col md={6}>
                            <Form.Group>
                                <Form.Label>Address</Form.Label>
                                <Form.Control
                                    as="textarea"
                                    name="address"
                                    rows={3}
                                    value={employeeForm.address}
                                    onChange={handleInputChange}
                                />
                            </Form.Group>
                        </Col>
                    </Row>

                    <Row>
                        <Col md={6}>
                            <Form.Group>
                                <Form.Label>Pincode</Form.Label>
                                <Form.Control
                                    type="text"
                                    name="pincode"
                                    value={employeeForm.pincode}
                                    onChange={handleInputChange}
                                />
                            </Form.Group>
                        </Col>

                        <Col md={6}>
                            <Form.Group>
                                <Form.Label>Area</Form.Label>
                                <Form.Control
                                    type="text"
                                    name="area"
                                    value={employeeForm.area}
                                    onChange={handleInputChange}
                                />
                            </Form.Group>
                        </Col>
                    </Row>

                    <Row>
                        <Col md={4}>
                            <Form.Group>
                                <Form.Label>City</Form.Label>
                                <Form.Control
                                    type="text"
                                    name="city"
                                    value={employeeForm.city}
                                    onChange={handleInputChange}
                                />
                            </Form.Group>
                        </Col>

                        <Col md={4}>
                            <Form.Group>
                                <Form.Label>State</Form.Label>
                                <Form.Control
                                    type="text"
                                    name="state"
                                    value={employeeForm.state}
                                    onChange={handleInputChange}
                                />
                            </Form.Group>
                        </Col>

                        <Col md={4}>
                            <Form.Group>
                                <Form.Label>Country</Form.Label>
                                <Form.Control
                                    type="text"
                                    name="country"
                                    value={employeeForm.country}
                                    onChange={handleInputChange}
                                />
                            </Form.Group>
                        </Col>
                    </Row>
                </Form>
            </Modal.Body>
            <Modal.Footer>
                <Button
                    variant="primary"
                    onClick={handleCreateOrUpdate}
                    disabled={isDataLoading || isSubmitting}
                >
                    {isSubmitting
                        ? editingEmployeeId
                            ? `Updating${".".repeat(dotCount)}`
                            : `Saving${".".repeat(dotCount)}`
                        : editingEmployeeId
                            ? "Update Employee"
                            : "Create Employee"}
                </Button>
                <Button variant="secondary" onClick={handleClose}>
                    Close
                </Button>
            </Modal.Footer>

            {isAlertVisible && (
                <AlertDialog
                    message={alertMessage}
                    title={alertTitle}
                    onClose={() => {
                        setIsAlertVisible(false);
                        if (alertTitle === 'Success') {
                            handleClose(); // Close only on success
                            handleCreateEmployee(); // Refresh the table
                        }
                    }}
                />
            )}
        </Modal>
    );
};

export default EmployeeCreateForm;
